'use client';

import { SvgChevrondown } from '@common/icon';
import { cn } from '@core/utils';
import { usePathname, useSearchParams } from 'next/navigation';
import { useState, useTransition } from 'react';

import type { SelectedRegion } from '@/components/client/RegionSelect/RegionPickerDialog';
import { RegionPickerDialog } from '@/components/client/RegionSelect/RegionPickerDialog';
import { useRegionMetaData } from '@/components/client/RegionSelect/useRegionMetaData';
import type { SEARCH_PARAMS_TYPE } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';

interface FestivalRegionSelectProps {
  value: SEARCH_PARAMS_TYPE.SELECTED_REGION;
  className?: string;
}

export const FestivalRegionSelect = ({
  value,
  className,
}: FestivalRegionSelectProps) => {
  const router = useCustomRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [opened, setOpened] = useState(false);
  const [isPending, startTransition] = useTransition();

  const regionMetaDataQuery = useRegionMetaData();
  const regionMetaData = regionMetaDataQuery.data;

  const selectedMainRegion = value.mainRegionCode
    ? regionMetaData?.mainRegionByCode.get(value.mainRegionCode)
    : undefined;
  const selectedSubRegion = value.subRegionCode
    ? regionMetaData?.subRegionByCode.get(value.subRegionCode)
    : undefined;
  const selectedRegionText = selectedSubRegion
    ? selectedSubRegion.value
    : selectedMainRegion
      ? `${selectedMainRegion.value} 전체`
      : '전국';

  const handleConfirm = ({ mainRegion, subRegion }: SelectedRegion) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    if (mainRegion?.code) nextSearchParams.set('codeRegion', mainRegion.code);
    else nextSearchParams.delete('codeRegion');

    if (subRegion?.code) nextSearchParams.set('codeSubRegion', subRegion.code);
    else nextSearchParams.delete('codeSubRegion');

    startTransition(() => {
      router.push(`${pathname}?${nextSearchParams.toString()}`, {
        scroll: false,
      });
    });
  };

  return (
    <div
      aria-label="축제 지역 선택"
      role="group"
      className={cn('flex shrink-0 items-center justify-end', className)}
    >
      <button
        aria-expanded={opened}
        aria-haspopup="dialog"
        aria-label={`지역 선택: 현재 ${selectedRegionText}`}
        className="flex max-w-220 items-center gap-4 rounded-xsmall px-6 py-4 text-left transition-colors duration-150 hover:bg-Gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500 disabled:opacity-50"
        disabled={regionMetaDataQuery.isLoading || isPending}
        onClick={() => setOpened(true)}
      >
        <span className="truncate font-600 text-Gray-700 typo-body4">
          {selectedRegionText}
        </span>
        <span
          aria-hidden="true"
          className="flex size-24 shrink-0 items-center justify-center"
        >
          <SvgChevrondown
            className="size-16 text-Gray-700"
            name="Arrow-Down-4"
          />
        </span>
      </button>
      <RegionPickerDialog
        opened={opened}
        onClose={() => setOpened(false)}
        onConfirm={handleConfirm}
        value={value}
        title="축제를 볼 지역을 선택해 주세요"
        enableWholeRegion
        enableWholeCountry
        wholeCountryHint="지역을 좁히면 내 위치에서 더 가까운 축제부터 볼 수 있어요."
      />
    </div>
  );
};
